import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { questionBanks, testSchemas } from '../api';
import { useToast } from '../components/Toast';

function CreateTestSchema() {
  const { bankId } = useParams();
  const navigate = useNavigate();
  const showToast = useToast();
  const [name, setName] = useState('');
  const [numQuestions, setNumQuestions] = useState(10);
  const [loading, setLoading] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  const [loadingBank, setLoadingBank] = useState(true);

  const parseTimeToSeconds = (timeStr) => {
    if (!timeStr) return 60;
    
    const asNumber = parseInt(timeStr);
    if (!isNaN(asNumber) && !String(timeStr).match(/[а-яА-Я]/)) {
      return asNumber;
    }
    
    const lowerStr = String(timeStr).toLowerCase().trim();
    const match = lowerStr.match(/(\d+)\s*(минут|минуты|минута|секунд|секунды|секунда)?/);
    if (match) {
      const value = parseInt(match[1]);
      const unit = match[2];
      
      if (!unit || unit.includes('минут')) {
        return value * 60;
      } else if (unit.includes('секунд')) {
        return value;
      }
      
      return value * 60;
    }
    
    return 60;
  };

  useEffect(() => {
    loadBankDetails();
  }, [bankId]);

  const loadBankDetails = async () => {
    try {
      const response = await questionBanks.getOne(bankId);
      setBankDetails(response.data);
      setLoadingBank(false);

      const totalQuestions = response.data.questions_count || response.data.questions?.length || 0;
      // Дефолт: 30 вопросов (или вся база, если в ней меньше). Пользователь меняет при желании.
      setNumQuestions(Math.min(30, totalQuestions) || 1);
      // Прединициализируем название — старт в один клик, поле остаётся редактируемым
      if (response.data.name) {
        setName(`Тест: ${response.data.name}`);
      }
    } catch (err) {
      console.error('Failed to load bank details', err);
      showToast('Ошибка загрузки базы вопросов', 'error');
      navigate('/test-schemas');
    }
  };

  const getTotalQuestions = () => {
    if (!bankDetails) return 0;
    // Try questions_count first, then questions array length as fallback
    return bankDetails.questions_count || bankDetails.questions?.length || 0;
  };

  const isValidQuestionCount = () => {
    const total = getTotalQuestions();
    return numQuestions > 0 && numQuestions <= total;
  };

  const calculateEstimatedTime = () => {
    if (!bankDetails || !bankDetails.questions || !Array.isArray(bankDetails.questions) || bankDetails.questions.length === 0 || numQuestions <= 0) {
      return { min: 0, max: 0 };
    }

    const questions = bankDetails.questions;
    const timesInSeconds = questions.map(q => parseTimeToSeconds(q.time_limit));
    
    if (timesInSeconds.length === 0) {
      return { min: 0, max: 0 };
    }
    
    // Average time for minimum estimate
    const avgSeconds = timesInSeconds.reduce((a, b) => a + b, 0) / timesInSeconds.length;
    const minEstimate = Math.round((avgSeconds * numQuestions) / 60);
    
    // Sum of N longest questions for maximum estimate
    const sortedTimes = [...timesInSeconds].sort((a, b) => b - a);
    const longestN = sortedTimes.slice(0, Math.min(numQuestions, sortedTimes.length));
    const maxEstimate = Math.round(longestN.reduce((a, b) => a + b, 0) / 60);
    
    return { min: minEstimate, max: maxEstimate };
  };

  const formatDuration = (minutes) => {
    if (minutes < 1) return '< 1 мин';
    if (minutes < 60) return `${minutes} мин`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours} ч ${mins} мин` : `${hours} ч`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValidQuestionCount()) {
      showToast(`Количество вопросов должно быть от 1 до ${getTotalQuestions()}`, 'error');
      return;
    }

    setLoading(true);

    try {
      const response = await testSchemas.create({
        name,
        question_bank_id: parseInt(bankId),
        num_questions: numQuestions,
      });
      // Переходим на страницу тест-схем с подсветкой созданной схемы
      navigate(`/test-schemas?highlight=${response.data.id}`);
    } catch (err) {
      showToast('Ошибка создания: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loadingBank) {
    return (
      <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div></div>
    );
  }

  const estimatedTime = calculateEstimatedTime();
  const timeRange = estimatedTime.min !== estimatedTime.max 
    ? `${formatDuration(estimatedTime.min)}–${formatDuration(estimatedTime.max)}`
    : formatDuration(estimatedTime.min);

  return (
    <div className="max-w-3xl">
      <div className="mb-6 md:mb-8">
        <button
          type="button"
          onClick={() => navigate('/test-schemas')}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium mb-2 inline-block"
        >
          ← Назад к тест-схемам
        </button>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Создание тест-схемы</h1>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-800 mt-4">
          <p className="font-semibold">
            База вопросов: {bankDetails.name}
          </p>
          <p className="text-sm mt-2">
            Всего вопросов в базе: <strong>{getTotalQuestions()}</strong>
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block mb-2 text-sm md:text-base text-gray-700 font-medium">Название тест-схемы</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Тест из 30 вопросов"
              required
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block mb-2 text-sm md:text-base text-gray-700 font-medium">
              Количество вопросов для кандидата (из {getTotalQuestions()})
              {estimatedTime.min > 0 && (
                <span className="ml-3 text-blue-600 font-normal">
                  ~{timeRange}
                </span>
              )}
            </label>
            <input
              type="number"
              value={numQuestions}
              onChange={(e) => setNumQuestions(parseInt(e.target.value) || 0)}
              min="1"
              max={getTotalQuestions()}
              placeholder="30"
              required
              className={`w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${!isValidQuestionCount() ? 'border-red-300' : 'border-gray-300'}`}
            />
            {!isValidQuestionCount() && (
              <p className="text-red-600 text-sm mt-2">
                Нельзя выбрать больше {getTotalQuestions()} {getTotalQuestions() === 1 ? 'вопроса' : getTotalQuestions() < 5 ? 'вопросов' : 'вопросов'}
              </p>
            )}
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">
              Вопросы выбираются с <strong>равномерным покрытием всех секций</strong> (случайно внутри каждой категории)
            </p>
          </div>

          <button
            type="submit"
            disabled={loading || !isValidQuestionCount()}
            title={!isValidQuestionCount() ? 'Укажите корректное количество вопросов' : ''}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Создание...' : 'Создать тест-схему'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CreateTestSchema;

